import { z } from 'zod';
import {
  CUSTOM_FIELD_ENTITIES,
  CUSTOM_FIELD_TYPES,
  type CustomFieldEntity,
  type EmailAccountProvider,
  type EmailAccountStatus,
  type CustomFieldType,
  LEAD_SOURCE_KINDS,
  type LeadSourceKind,
  SCORE_CATEGORIES,
  type SendAttemptOutcome,
  type StageKind,
  USER_ROLES,
  type UserRole,
} from '../constants';
import { type AppSettings, SETTINGS_KEYS, type SettingsKey } from '../settings';
import { NullableText, RequiredText } from './common';
import {
  COLOR_TOKENS,
  type CustomFieldDefinitionDTO,
  type LeadSourceDTO,
  type ProductCategoryDTO,
  type ProductDTO,
  type RelationshipDTO,
  type ScoringRuleDTO,
  type StageDTO,
  type TagDTO,
  type TierDTO,
} from './meta';

/**
 * Administration (Settings) contracts: typed settings, users, territories, scoring rules/tiers, pipeline
 * stages, relationship statuses and the other admin-managed lookups. Shared by the API (validation) and the
 * web app (client-side validation + DTO types).
 */

export type ValidationIssue = { path: string; message: string };

/** Report dotted-path issues from the pure rule functions on a zod refinement context. */
function reportIssues(ctx: z.RefinementCtx, issues: ValidationIssue[]): void {
  for (const issue of issues) {
    const path = issue.path.split('.').map((p) => (/^\d+$/.test(p) ? Number(p) : p));
    ctx.addIssue({ code: 'custom', message: issue.message, path });
  }
}

// ---------------------------------------------------------------------------------------------------------
// Keys and small primitives
// ---------------------------------------------------------------------------------------------------------

/** Machine keys for admin-defined lookups (stages, statuses, sources, custom fields, custom signals…). */
export const LOOKUP_KEY_PATTERN = /^[a-z][a-z0-9_]{1,59}$/;
export const LookupKey = z
  .string()
  .trim()
  .regex(LOOKUP_KEY_PATTERN, 'Use 2–60 lowercase letters, digits or underscores, starting with a letter');

/** "Attended trade show!" → "attended_trade_show" (accents stripped, so "Pizzéria" → "pizzeria"). */
export function slugifyKey(label: string): string {
  let key = label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60)
    .replace(/_+$/g, '');
  if (!/^[a-z]/.test(key)) key = `k_${key}`.slice(0, 60);
  return key.length >= 2 ? key : `${key}_x`;
}

export const ColorTokenSchema = z.enum(COLOR_TOKENS);

/** `{ ids }` in display order, for drag/arrow reordering. */
export const SettingsReorderBody = z.object({ ids: z.array(z.uuid()).min(1).max(500) });
export type SettingsReorderBody = z.infer<typeof SettingsReorderBody>;

/** Deleting something still referenced by leads/values needs an explicit confirmation from the admin. */
export const ConfirmDeleteBody = z.object({ confirm: z.boolean().optional() }).default({});
export type ConfirmDeleteBody = z.infer<typeof ConfirmDeleteBody>;

// ---------------------------------------------------------------------------------------------------------
// Typed settings (app_settings)
// ---------------------------------------------------------------------------------------------------------

export const SettingsKeyParams = z.object({ key: z.enum(SETTINGS_KEYS as [SettingsKey, ...SettingsKey[]]) });

export type UpdateSettingResponse<K extends SettingsKey = SettingsKey> = {
  key: K;
  value: AppSettings[K];
  /** True when the change affects derived lead state and a background recompute was queued. */
  recomputeQueued: boolean;
};

function isValidLocale(locale: string): boolean {
  try {
    return Intl.getCanonicalLocales(locale).length === 1;
  } catch {
    return false;
  }
}

/**
 * Cross-field rules the per-key zod schemas cannot express. Pure, so the settings forms run it before
 * saving and the API runs it again. (Timezone validity and tier-key existence are checked server-side.)
 */
export function settingsIssues<K extends SettingsKey>(key: K, value: AppSettings[K]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  switch (key) {
    case 'general': {
      const v = value as AppSettings['general'];
      if (!/^[A-Z]{3}$/.test(v.defaultCurrency)) issues.push({ path: 'defaultCurrency', message: 'Use a 3-letter ISO currency code, e.g. CAD' });
      if (!/^[A-Z]{2}$/.test(v.defaultCountry)) issues.push({ path: 'defaultCountry', message: 'Use a 2-letter ISO country code, e.g. CA' });
      if (!isValidLocale(v.locale)) issues.push({ path: 'locale', message: 'Unknown locale, e.g. en-CA or fr-CA' });
      break;
    }
    case 'sending': {
      const v = value as AppSettings['sending'];
      if (v.sendWindow.enabled && v.sendWindow.startHour >= v.sendWindow.endHour) {
        issues.push({ path: 'sendWindow.endHour', message: 'End hour must be after the start hour' });
      }
      if (new Set(v.sendWindow.days).size !== v.sendWindow.days.length) {
        issues.push({ path: 'sendWindow.days', message: 'Each day can only be listed once' });
      }
      if (v.perUserDailyLimit > v.dailyLimit) {
        issues.push({ path: 'perUserDailyLimit', message: 'Cannot exceed the organization daily limit' });
      }
      break;
    }
    case 'duplicates': {
      const v = value as AppSettings['duplicates'];
      if (v.likelyThreshold < v.possibleThreshold) {
        issues.push({ path: 'likelyThreshold', message: 'Must be at least the "possible duplicate" threshold' });
      }
      break;
    }
    case 'opportunities': {
      const v = value as AppSettings['opportunities'];
      if (v.lostReasons.length === 0) issues.push({ path: 'lostReasons', message: 'Keep at least one lost reason' });
      const seen = new Set<string>();
      v.lostReasons.forEach((reason, index) => {
        const normalized = reason.trim().toLowerCase();
        if (normalized === '') issues.push({ path: `lostReasons.${index}`, message: 'Required' });
        else if (seen.has(normalized)) issues.push({ path: `lostReasons.${index}`, message: 'Duplicate reason' });
        seen.add(normalized);
      });
      break;
    }
    case 'nextAction': {
      const v = value as AppSettings['nextAction'];
      if (new Set(v.priorityTierKeys).size !== v.priorityTierKeys.length) {
        issues.push({ path: 'priorityTierKeys', message: 'Each tier can only be listed once' });
      }
      break;
    }
    default:
      break;
  }
  return issues;
}

// ---------------------------------------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------------------------------------

const TemporaryPassword = z.string().min(10, 'Use at least 10 characters').max(200);

export const CreateUserBody = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email address').max(320),
  name: RequiredText(120),
  role: z.enum(USER_ROLES),
  title: NullableText(120).optional(),
  phone: NullableText(40).optional(),
  /** IANA zone (validated server-side); null → organization default. */
  timezone: NullableText(64).optional(),
  temporaryPassword: TemporaryPassword,
});
export type CreateUserBody = z.infer<typeof CreateUserBody>;

export const UpdateUserBody = z.object({
  name: RequiredText(120).optional(),
  role: z.enum(USER_ROLES).optional(),
  title: NullableText(120).optional(),
  phone: NullableText(40).optional(),
  timezone: NullableText(64).optional(),
  emailSignature: NullableText(4000).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateUserBody = z.infer<typeof UpdateUserBody>;

export const ResetPasswordBody = z.object({ temporaryPassword: TemporaryPassword });

/** PATCH /api/users/me — any signed-in user edits their own profile. */
export const UpdateMyProfileBody = z.object({
  name: RequiredText(120).optional(),
  title: NullableText(120).optional(),
  phone: NullableText(40).optional(),
  timezone: NullableText(64).optional(),
  emailSignature: NullableText(4000).optional(),
});
export type UpdateMyProfileBody = z.infer<typeof UpdateMyProfileBody>;

export type AdminUserDTO = {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  title: string | null;
  phone: string | null;
  timezone: string | null;
  emailSignature: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  /** Set while the account is locked after too many failed sign-ins. */
  lockedUntil: string | null;
  createdAt: string;
  /** Live leads owned by this user (they stay assigned when the user is deactivated). */
  ownedLeadCount: number;
  territoryIds: string[];
};

export type UpdateUserResponse = { user: AdminUserDTO; sessionsRevoked: boolean };

// ---------------------------------------------------------------------------------------------------------
// Territories
// ---------------------------------------------------------------------------------------------------------

export const TerritoryRegionInput = z.object({
  country: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'Use a 2-letter country code, e.g. CA'),
  /** Province/state code or name ('QC', 'Ontario'); normalized server-side. Empty = whole country. */
  region: NullableText(80).optional(),
  /** Empty = whole region. */
  city: NullableText(120).optional(),
});
export type TerritoryRegionInput = z.infer<typeof TerritoryRegionInput>;

export const CreateTerritoryBody = z.object({
  name: RequiredText(120),
  description: NullableText(500).optional(),
  defaultOwnerId: z.uuid().nullable().optional(),
  regions: z.array(TerritoryRegionInput).max(200).default([]),
  memberIds: z.array(z.uuid()).max(500).default([]),
});
export type CreateTerritoryBody = z.infer<typeof CreateTerritoryBody>;

/** Provided arrays replace the current regions / members. */
export const UpdateTerritoryBody = z.object({
  name: RequiredText(120).optional(),
  description: NullableText(500).optional(),
  defaultOwnerId: z.uuid().nullable().optional(),
  regions: z.array(TerritoryRegionInput).max(200).optional(),
  memberIds: z.array(z.uuid()).max(500).optional(),
});
export type UpdateTerritoryBody = z.infer<typeof UpdateTerritoryBody>;

export type TerritoryRegionDTO = { id: string; country: string; region: string | null; city: string | null };
export type TerritoryAdminDTO = {
  id: string;
  name: string;
  description: string | null;
  defaultOwnerId: string | null;
  regions: TerritoryRegionDTO[];
  memberIds: string[];
  /** Live leads currently assigned to this territory. */
  leadCount: number;
};
/** Leads in the territory's area that had no territory now have this one; unowned ones got the default owner. */
export type ApplyTerritoryResponse = { assigned: number; ownersSet: number };

// ---------------------------------------------------------------------------------------------------------
// Scoring rules and tiers
// ---------------------------------------------------------------------------------------------------------

const Points = z.number().int().min(-100, 'Minimum −100').max(100, 'Maximum 100');
const DecayDays = z.number().int().min(1).max(3650).nullable();
const MaxOccurrences = z.number().int().min(1).max(100);

export const UpdateScoringRuleBody = z.object({
  points: Points.optional(),
  isEnabled: z.boolean().optional(),
  /** Signal rules only: signals older than this stop counting; null = never expire. */
  decayDays: DecayDays.optional(),
  /** Signal rules only. */
  maxOccurrences: MaxOccurrences.optional(),
  /** Attribute rules: validated against the rule's params schema; tag rules: `{ tagId }`. */
  params: z.record(z.string(), z.unknown()).optional(),
  label: RequiredText(120).optional(),
  description: NullableText(500).optional(),
});
export type UpdateScoringRuleBody = z.infer<typeof UpdateScoringRuleBody>;

export const CreateScoringRuleBody = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('tag'),
    tagId: z.uuid(),
    /** Defaults to the tag name. */
    label: RequiredText(120).optional(),
    description: NullableText(500).optional(),
    category: z.enum(SCORE_CATEGORIES).default('business_fit'),
    points: Points,
  }),
  z.object({
    kind: z.literal('signal'),
    /** Defaults to a slug of the label, e.g. "attended_trade_show". */
    key: LookupKey.optional(),
    label: RequiredText(120),
    description: NullableText(500).optional(),
    category: z.enum(SCORE_CATEGORIES),
    points: Points,
    decayDays: DecayDays.default(365),
    maxOccurrences: MaxOccurrences.default(1),
  }),
]);
export type CreateScoringRuleBody = z.infer<typeof CreateScoringRuleBody>;

export type ScoringParamMeta = { label: string; default: number | string[] };
export type ScoringRuleAdminDTO = ScoringRuleDTO & {
  /** Built-in attribute rules: what the rule checks and its editable params. */
  builtIn: { description: string; params: Record<string, ScoringParamMeta> } | null;
  /** Signal rules: live (non-voided) signals recorded under this key. */
  signalCount: number;
};
export type ScoringRulesResponse = { rules: ScoringRuleAdminDTO[] };
export type ScoringRuleMutationResponse = { rule: ScoringRuleAdminDTO; recomputeQueued: true };
export type RecomputeQueuedResponse = { recomputeQueued: true };

export const TierInput = z.object({
  key: LookupKey,
  label: RequiredText(60),
  minScore: z.number().int().min(0, 'Minimum 0').max(100, 'Maximum 100'),
  color: ColorTokenSchema,
  description: NullableText(300).optional(),
});
export type TierInput = z.infer<typeof TierInput>;

/** Tier-set rules: unique keys, unique thresholds, exactly one tier starting at 0 (gaps are fine). */
export function tierIssues(tiers: readonly { key: string; minScore: number }[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const keys = new Map<string, number>();
  const scores = new Map<number, number>();
  tiers.forEach((tier, index) => {
    if (keys.has(tier.key)) issues.push({ path: `tiers.${index}.key`, message: 'Each tier needs a unique key' });
    else keys.set(tier.key, index);
    if (scores.has(tier.minScore)) {
      issues.push({ path: `tiers.${index}.minScore`, message: `Another tier already starts at ${tier.minScore}` });
    } else scores.set(tier.minScore, index);
  });
  if (!tiers.some((t) => t.minScore === 0)) {
    issues.push({ path: 'tiers', message: 'One tier must start at 0 so every score has a tier' });
  }
  return issues;
}

export const UpdateTiersBody = z
  .object({ tiers: z.array(TierInput).min(1).max(12) })
  .superRefine((body, ctx) => reportIssues(ctx, tierIssues(body.tiers)));
export type UpdateTiersBody = z.infer<typeof UpdateTiersBody>;

export type TierAdminDTO = TierDTO & {
  /** Built-in tiers can be relabeled, recolored and moved, but not removed. */
  isSystem: boolean;
  leadCount: number;
};
export type TiersResponse = { tiers: TierAdminDTO[] };

// ---------------------------------------------------------------------------------------------------------
// Pipeline stages and relationship statuses
// ---------------------------------------------------------------------------------------------------------

/** Kinds an admin can give a custom stage ('do_not_contact' is reserved for the built-in stage). */
export const CUSTOM_STAGE_KINDS = ['open', 'won', 'lost', 'dormant'] as const satisfies readonly StageKind[];

export const STAGE_KIND_LABELS: Record<StageKind, string> = {
  open: 'Open',
  won: 'Won',
  lost: 'Lost',
  dormant: 'Dormant',
  do_not_contact: 'Do not contact',
};

export const CreateStageBody = z.object({
  label: RequiredText(60),
  key: LookupKey.optional(),
  color: ColorTokenSchema.default('slate'),
  kind: z.enum(CUSTOM_STAGE_KINDS).default('open'),
});
export type CreateStageBody = z.infer<typeof CreateStageBody>;

export const UpdateStageBody = z.object({
  label: RequiredText(60).optional(),
  color: ColorTokenSchema.optional(),
  isActive: z.boolean().optional(),
  /** Custom stages only. */
  kind: z.enum(CUSTOM_STAGE_KINDS).optional(),
  /** Keys are immutable; present only so a changed key is rejected explicitly. */
  key: z.string().optional(),
});
export type UpdateStageBody = z.infer<typeof UpdateStageBody>;

export const DeleteStageBody = z.object({ reassignToStageId: z.uuid().optional() }).default({});

export type StageAdminDTO = StageDTO & { leadCount: number };

export const CreateRelationshipBody = z.object({
  label: RequiredText(60),
  key: LookupKey.optional(),
  color: ColorTokenSchema.default('slate'),
  blocksOutreach: z.boolean().default(false),
});
export type CreateRelationshipBody = z.infer<typeof CreateRelationshipBody>;

export const UpdateRelationshipBody = z.object({
  label: RequiredText(60).optional(),
  color: ColorTokenSchema.optional(),
  isActive: z.boolean().optional(),
  blocksOutreach: z.boolean().optional(),
  key: z.string().optional(),
});
export type UpdateRelationshipBody = z.infer<typeof UpdateRelationshipBody>;

export const DeleteRelationshipBody = z.object({ reassignToStatusId: z.uuid().optional() }).default({});

export type RelationshipAdminDTO = RelationshipDTO & { leadCount: number };

// ---------------------------------------------------------------------------------------------------------
// Lead sources, product catalog, tags, custom fields
// ---------------------------------------------------------------------------------------------------------

export const LEAD_SOURCE_KIND_LABELS: Record<LeadSourceKind, string> = {
  manual: 'Manual entry',
  csv_import: 'CSV import',
  discovery: 'Discovery',
  referral: 'Referral',
  event: 'Event / trade show',
  inbound: 'Inbound inquiry',
  other: 'Other',
};

export const CreateLeadSourceBody = z.object({
  name: RequiredText(80),
  key: LookupKey.optional(),
  kind: z.enum(LEAD_SOURCE_KINDS).default('other'),
});
export type CreateLeadSourceBody = z.infer<typeof CreateLeadSourceBody>;

export const UpdateLeadSourceBody = z.object({
  name: RequiredText(80).optional(),
  kind: z.enum(LEAD_SOURCE_KINDS).optional(),
  isActive: z.boolean().optional(),
  key: z.string().optional(),
});
export type UpdateLeadSourceBody = z.infer<typeof UpdateLeadSourceBody>;

export type LeadSourceAdminDTO = LeadSourceDTO & {
  leadCount: number;
  /** Used by imports/discovery; can be renamed or deactivated but not deleted. */
  isBuiltIn: boolean;
};

export const CreateProductCategoryBody = z.object({ label: RequiredText(80), key: LookupKey.optional() });
export type CreateProductCategoryBody = z.infer<typeof CreateProductCategoryBody>;
export const UpdateProductCategoryBody = z.object({
  label: RequiredText(80).optional(),
  isActive: z.boolean().optional(),
  key: z.string().optional(),
});
export type UpdateProductCategoryBody = z.infer<typeof UpdateProductCategoryBody>;
export type ProductCategoryAdminDTO = ProductCategoryDTO & { productCount: number; interestCount: number };

const Money = z.number().min(0, 'Cannot be negative').max(10_000_000).multipleOf(0.01, 'Use at most 2 decimals');

export const CreateProductBody = z.object({
  name: RequiredText(200),
  sku: NullableText(60).optional(),
  categoryKey: z.string().min(1, 'Required'),
  listPrice: Money.nullable().optional(),
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'Use a 3-letter currency code')
    .default('CAD'),
  description: NullableText(1000).optional(),
  isActive: z.boolean().default(true),
});
export type CreateProductBody = z.infer<typeof CreateProductBody>;

export const UpdateProductBody = z.object({
  name: RequiredText(200).optional(),
  sku: NullableText(60).optional(),
  categoryKey: z.string().min(1, 'Required').optional(),
  listPrice: Money.nullable().optional(),
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'Use a 3-letter currency code')
    .optional(),
  description: NullableText(1000).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateProductBody = z.infer<typeof UpdateProductBody>;

export type ProductAdminDTO = ProductDTO & { description: string | null; opportunityLineCount: number };

export const CreateTagBody = z.object({ name: RequiredText(60), color: ColorTokenSchema.default('slate') });
export type CreateTagBody = z.infer<typeof CreateTagBody>;
export const UpdateTagBody = z.object({ name: RequiredText(60).optional(), color: ColorTokenSchema.optional() });
export type UpdateTagBody = z.infer<typeof UpdateTagBody>;
export type TagAdminDTO = TagDTO & { leadCount: number; scoringRuleCount: number };

export const CUSTOM_FIELD_ENTITY_LABELS: Record<CustomFieldEntity, string> = {
  business: 'Lead (business)',
  contact: 'Contact',
  opportunity: 'Opportunity',
};

export const CUSTOM_FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  boolean: 'Yes / no',
  select: 'Dropdown',
};

const SelectOptions = z.array(z.string().trim().min(1, 'Required').max(100)).max(100);

function optionIssues(type: CustomFieldType | undefined, options: string[] | undefined): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (type === 'select' && options !== undefined && options.length === 0) {
    issues.push({ path: 'options', message: 'Add at least one option' });
  }
  const seen = new Set<string>();
  options?.forEach((option, index) => {
    const normalized = option.toLowerCase();
    if (seen.has(normalized)) issues.push({ path: `options.${index}`, message: 'Duplicate option' });
    seen.add(normalized);
  });
  return issues;
}

export const CreateCustomFieldBody = z
  .object({
    entity: z.enum(CUSTOM_FIELD_ENTITIES),
    label: RequiredText(80),
    key: LookupKey.optional(),
    type: z.enum(CUSTOM_FIELD_TYPES),
    options: SelectOptions.default([]),
    isRequired: z.boolean().default(false),
    isActive: z.boolean().default(true),
  })
  .superRefine((body, ctx) => reportIssues(ctx, optionIssues(body.type, body.type === 'select' ? body.options : undefined)));
export type CreateCustomFieldBody = z.infer<typeof CreateCustomFieldBody>;

export const UpdateCustomFieldBody = z
  .object({
    label: RequiredText(80).optional(),
    type: z.enum(CUSTOM_FIELD_TYPES).optional(),
    options: SelectOptions.optional(),
    isRequired: z.boolean().optional(),
    isActive: z.boolean().optional(),
    key: z.string().optional(),
  })
  .superRefine((body, ctx) => reportIssues(ctx, optionIssues(body.type, body.options)));
export type UpdateCustomFieldBody = z.infer<typeof UpdateCustomFieldBody>;

export type CustomFieldAdminDTO = CustomFieldDefinitionDTO & { valueCount: number };

// ---------------------------------------------------------------------------------------------------------
// Email provider
// ---------------------------------------------------------------------------------------------------------

export type EmailProviderStatusDTO = {
  provider: string;
  configured: boolean;
  fromAddress: string;
  fromName: string;
  /** Where to point the provider's bounce/complaint/inbound webhooks (append ?token=<EMAIL_WEBHOOK_SECRET>). */
  webhookUrl: string;
  webhookSecretConfigured: boolean;
  /** Connectivity/credential check, when the provider supports one. */
  verify: { ok: boolean; message: string } | null;
};

export const TestEmailBody = z.object({ to: z.string().trim().toLowerCase().email('Enter a valid email address').max(320) });

// ---------------------------------------------------------------------------------------------------------
// Sending mailboxes (send as a person's own Gmail / SMTP account)
// ---------------------------------------------------------------------------------------------------------

export const EMAIL_ACCOUNT_PROVIDER_LABELS: Record<EmailAccountProvider, string> = {
  google: 'Google / Gmail',
  smtp: 'Other mailbox (SMTP)',
  microsoft: 'Microsoft 365 / Outlook',
};

/** What a person sees about a mailbox they (or someone) connected. Credentials are never included. */
export type EmailMailboxDTO = {
  id: string;
  userId: string;
  /** True for the mailbox of the person making the request — the one their own email is sent from. */
  isMine: boolean;
  /** Filled for administrators looking at other people's mailboxes. */
  ownerName: string | null;
  provider: EmailAccountProvider;
  emailAddress: string;
  displayName: string | null;
  status: EmailAccountStatus;
  /** Why the last send or check failed; null when healthy. */
  lastError: string | null;
  lastVerifiedAt: string | null;
  lastUsedAt: string | null;
  /** Also sends automated and bulk email; new leads are spread across all such mailboxes, each lead keeps its own. */
  useForOutreach: boolean;
  /** Replies are read from this mailbox (IMAP configured). */
  canReceive: boolean;
  /** Last successful reply check, and why the last one failed (null when it worked). */
  lastSyncedAt: string | null;
  syncError: string | null;
  createdAt: string;
};

/** Google connect needs an OAuth app on the server; when it is missing the UI explains what to create. */
export type GoogleConnectInfoDTO = { configured: boolean; redirectUri: string; setup: string | null };

/**
 * Microsoft 365 / Outlook.com connect ("Sign in with Microsoft"): sends and reads replies through Microsoft Graph.
 * Needs an app registration (MICROSOFT_CLIENT_ID/SECRET); `setup` holds the steps when it is missing.
 */
export type MicrosoftConnectInfoDTO = { configured: boolean; redirectUri: string; setup: string | null };

export type EmailMailboxesResponse = { items: EmailMailboxDTO[]; google: GoogleConnectInfoDTO; microsoft: MicrosoftConnectInfoDTO };

const SmtpHost = z.string().trim().min(1, 'Enter the mail server').max(255);
const SmtpPort = z.number().int().min(1).max(65535);
const SmtpPassword = z.string().min(1, 'Enter the password or app password').max(500);

/**
 * POST /api/settings/email-mailboxes/smtp — any mailbox with a password or app password: SMTP to send and, when
 * `imap` is given, IMAP to read replies (Gmail app passwords work for both; the web app pre-fills Gmail's servers).
 */
export const ConnectSmtpMailboxBody = z.object({
  emailAddress: z.string().trim().toLowerCase().email('Enter the address mail should come from').max(320),
  displayName: NullableText(120).optional(),
  host: SmtpHost,
  port: SmtpPort.default(465),
  /** true for implicit TLS (port 465), false for STARTTLS (port 587). */
  secure: z.boolean().default(true),
  user: z.string().trim().min(1, 'Enter the login').max(320),
  password: SmtpPassword,
  /** Incoming server for reading replies; same login and password. Omit/null = send only. */
  imap: z
    .object({
      host: z.string().trim().min(1, 'Enter the incoming mail server').max(255),
      port: SmtpPort.default(993),
      secure: z.boolean().default(true),
    })
    .nullish(),
  useForOutreach: z.boolean().default(false),
});
/** Parsed shape (port/secure are filled in by the defaults), which is what the service receives. */
export type ConnectSmtpMailboxBody = z.infer<typeof ConnectSmtpMailboxBody>;

/** PATCH /api/settings/email-mailboxes/:id */
export const UpdateMailboxBody = z.object({ useForOutreach: z.boolean() });
export type UpdateMailboxBody = z.infer<typeof UpdateMailboxBody>;

export const MailboxTestBody = z.object({ to: z.string().trim().toLowerCase().email('Enter a valid email address').max(320) });
export type MailboxTestBody = z.infer<typeof MailboxTestBody>;

export type MailboxVerifyResult = { ok: boolean; message: string };

export type TestEmailResult = {
  outcome: SendAttemptOutcome;
  providerMessageId: string | null;
  error: string | null;
};
